import type {
    AccountNav
} from '~/types';
import {
    mdiHome,
    mdiAccount,
    mdiBookOpenBlankVariant,
    mdiTshirtCrew,
    mdiBellCog,
    mdiTruckCargoContainer,
    mdiInvoiceTextClock,
    mdiPackageVariantClosed,
    mdiTruckFast,
    mdiFormatListChecks,
    mdiShieldEdit,
    mdiFrequentlyAskedQuestions,
    mdiCashRefund,
    mdiPhoneMessage,
    mdiInvoiceTextMultiple,
    mdiReceiptTextOutline,
    mdiBank,
    mdiAccountCash,
    mdiCashSync,
    mdiWalletBifold,
    mdiListBox,
    mdiGift,
    mdiMultimedia,
    mdiFileTree,
    mdiBookOpen,
    mdiStoreSearch,
    mdiFileSign,
    mdiAccountGroup,
    mdiShieldHalfFull,
    mdiAccountSupervisorCircle,
    mdiPencilRuler
} from '@mdi/js';

const data: AccountNav = {
    account: {
        title: 'Account',
        hideTitleInMenu: true,
        items: [
            {
                id: 'account-home',
                title: 'Account Home',
                value: 1,
                link: {
                    personal: {
                        to: '/account'
                    },
                    entity: {
                        to: '/account'
                    }
                },
                hideInHome: true,
                icon: mdiHome
            },
            {
                id: 'profile',
                title: 'Profile',
                value: 10,
                link: {
                    personal: {
                        to: '/account/profile'
                    },
                    entity: {
                        to: '/account/profile',
                        permissions: false
                    }
                },
                icon: mdiAccount
            },
            {
                id: 'bodies',
                title: 'Bodies',
                value: 20,
                link: {
                    personal: {
                        to: '/account/bodies'
                    },
                    entity: {
                        to: '/account/bodies',
                        permissions: false
                    }
                },
                icon: mdiTshirtCrew
            },
            {
                id: 'email-preferences',
                title: 'Email Preferences',
                value: 30,
                link: {
                    personal: {
                        to: '/account/subscriptions'
                    },
                    entity: {
                        to: '/account/subscriptions',
                        permissions: false
                    }
                },
                icon: mdiBellCog
            },
            {
                id: 'manage-members',
                title: 'Manage Members',
                value: 40,
                link: {
                    personal: {
                        to: '/account/manage-members',
                        permissions: false
                    },
                    entity: {
                        to: '/account/manage-members',
                        permissions: {
                            page: 'manage_members'
                        }
                    }
                },
                icon: mdiAccountSupervisorCircle
            },
            {
                id: 'customizations',
                title: 'Customizations',
                value: 40,
                link: {
                    personal: {
                        to: '/account/customizations'
                    },
                    entity: {
                        to: '/account/customizations'
                    }
                },
                icon: mdiPencilRuler
            }
        ]
    },
    purchases: {
        title: 'Purchases',
        items: [
            {
                id: 'bulk-order',
                title: 'Bulk Order Form',
                value: 50,
                link: {
                    personal: {
                        to: '/account/bulk-order',
                        permissions: false
                    },
                    entity: {
                        to: '/account/bulk-order',
                        permissions: {
                            page: 'bulk_order_form'
                        }
                    }
                },
                icon: mdiTruckCargoContainer
            },
            {
                id: 'orders',
                title: 'Order History',
                value: 60,
                link: {
                    personal: {
                        to: '/account/orders'
                    },
                    entity: {
                        to: '/account/orders',
                        permissions: {
                            resource: 'orders'
                        }
                    }
                },
                icon: mdiInvoiceTextClock
            },
            {
                id: 'order-lines',
                title: 'Order Lines',
                value: 65,
                link: {
                    personal: {
                        to: '/account/order-lines'
                    },
                    entity: {
                        to: '/account/order-lines',
                        permissions: {
                            resource: 'orders'
                        }
                    }
                },
                icon: mdiPackageVariantClosed
            },
            {
                id: 'shipments',
                title: 'Shipments',
                value: 67,
                link: {
                    personal: {
                        to: '/account/shipments'
                    },
                    entity: {
                        to: '/account/shipments',
                        permissions: {
                            resource: 'orders'
                        }
                    }
                },
                icon: mdiTruckFast
            },
            {
                id: 'addresses',
                title: 'Addresses',
                value: 70,
                link: {
                    personal: {
                        to: '/account/addresses'
                    },
                    entity: {
                        to: '/account/addresses',
                        permissions: {
                            resource: 'addresses'
                        }
                    }
                },
                icon: mdiBookOpenBlankVariant
            },
            {
                id: 'registration',
                title: 'Product Registrations',
                value: 80,
                link: {
                    personal: {
                        to: '/account/registrations'
                    },
                    entity: {
                        to: '/account/registrations',
                        permissions: {
                            resource: 'productRegistrations'
                        }
                    }
                },
                icon: mdiFormatListChecks
            },
            {
                id: 'claims',
                title: 'Warranty Claims',
                value: 90,
                link: {
                    personal: {
                        to: '/account/claims'
                    },
                    entity: {
                        to: '/account/claims',
                        permissions: {
                            resource: 'warrantyClaims'
                        }
                    }
                },
                icon: mdiShieldEdit
            }
        ]
    },
    billing: {
        title: 'Billing',
        items: [
            {
                id: 'invoices',
                title: 'Invoices',
                value: 100,
                link: {
                    personal: {
                        to: '/account/invoices'
                    },
                    entity: {
                        to: '/account/invoices',
                        permissions: {
                            resource: 'invoices'
                        }
                    }
                },
                icon: mdiInvoiceTextMultiple
            },
            {
                id: 'credit-memos',
                title: 'Credit Memos',
                value: 105,
                link: {
                    personal: {
                        to: '/account/credit-memos'
                    },
                    entity: {
                        to: '/account/credit-memos',
                        permissions: {
                            resource: 'invoices'
                        }
                    }
                },
                icon: mdiReceiptTextOutline
            },
            {
                id: 'payment-methods',
                title: 'Payment Methods',
                value: 110,
                link: {
                    personal: {
                        to: '/account/payment-methods'
                    },
                    entity: {
                        to: '/account/payment-methods',
                        permissions: {
                            resource: 'paymentMethods'
                        }
                    }
                },
                icon: mdiBank
            },
            {
                id: 'autopay',
                title: 'Autopay',
                value: 110,
                link: {
                    personal: {
                        to: '/account/autopay'
                    },
                    entity: {
                        to: '/account/autopay',
                        permissions: {
                            flag: 'autopay_enabled'
                        }
                    }
                },
                icon: mdiCashSync
            },
            {
                id: 'pricing-terms',
                title: 'Pricing & Terms',
                value: 120,
                link: {
                    personal: {
                        to: '/account/pricing-terms'
                    },
                    entity: {
                        to: '/account/pricing-terms'
                    }
                },
                icon: mdiAccountCash
            },
            {
                id: 'balance',
                title: 'Account Balance',
                value: 130,
                link: {
                    personal: {
                        to: '/account/balance'
                    },
                    entity: {
                        to: '/account/balance',
                        permissions: {
                            resource: 'orders'
                        }
                    }
                },
                icon: mdiWalletBifold
            }
        ]
    },
    products: {
        title: 'Products',
        items: [
            {
                id: 'product-list',
                title: 'Product List',
                value: 140,
                link: {
                    personal: {
                        to: '/account/product-list',
                        permissions: {
                            page: 'product_list'
                        }
                    },
                    entity: {
                        to: '/account/product-list',
                        permissions: {
                            page: 'product_list'
                        }
                    }
                },
                icon: mdiListBox
            },
            {
                id: 'sku-list',
                title: 'SKU List',
                value: 150,
                link: {
                    personal: {
                        to: '/account/sku-list',
                        permissions: {
                            page: 'sku_list'
                        }
                    },
                    entity: {
                        to: '/account/sku-list',
                        permissions: {
                            page: 'sku_list'
                        }
                    }
                },
                icon: mdiFileTree
            },
            {
                id: 'product-launches',
                title: 'New Products',
                value: 160,
                link: {
                    personal: {
                        to: '/account/product-launches?assortment_type=new&ordering=-launch_date',
                        permissions: {
                            page: 'product_launches'
                        }
                    },
                    entity: {
                        to: '/account/product-launches?assortment_type=new&ordering=-launch_date',
                        permissions: {
                            page: 'product_launches'
                        }
                    }
                },
                icon: mdiGift
            },
            {
                id: 'new-skus',
                title: 'New SKUs',
                value: 165,
                link: {
                    personal: {
                        to: '/account/sku-launches?assortment_type=new&ordering=-launch_date',
                        permissions: {
                            page: 'sku_list'
                        }
                    },
                    entity: {
                        to: '/account/sku-launches?assortment_type=new&ordering=-launch_date',
                        permissions: {
                            page: 'sku_list'
                        }
                    }
                },
                icon: mdiGift
            },
            {
                id: 'marketing-assets',
                title: 'Marketing Assets',
                value: 170,
                link: {
                    personal: {
                        to: '/account/marketing-assets?has_marketing_assets=true',
                        permissions: {
                            page: 'marketing_assets'
                        }
                    },
                    entity: {
                        to: '/account/marketing-assets?has_marketing_assets=true',
                        permissions: {
                            page: 'marketing_assets'
                        }
                    }
                },
                icon: mdiMultimedia
            },
            {
                id: 'product-catalog',
                title: 'Product Catalog',
                value: 180,
                link: {
                    personal: {
                        to: '/product-catalog'
                    },
                    entity: {
                        to: '/product-catalog'
                    }
                },
                icon: mdiBookOpen
            },
            {
                id: 'store-locator',
                title: 'Store Locator',
                value: 190,
                link: {
                    personal: {
                        to: '/retailers/stores/'
                    },
                    entity: {
                        to: '/retailers/stores/'
                    }
                },
                icon: mdiStoreSearch
            }
        ]
    },
    compliance: {
        title: 'Compliance',
        items: [
            {
                id: 'agreements',
                title: 'Agreements',
                value: 200,
                link: {
                    personal: {
                        to: '/account/agreements',
                        permissions: false
                    },
                    entity: {
                        to: '/account/agreements',
                        permissions: {
                            page: 'agreements'
                        }
                    }
                },
                icon: mdiFileSign
            },
            {
                id: 'map-policy',
                title: 'MAP Policy',
                value: 210,
                link: {
                    personal: {
                        to: '/account/map-policy',
                        permissions: false
                    },
                    entity: {
                        to: '/account/map-policy',
                        permissions: {
                            page: 'map_policy'
                        }
                    }
                },
                icon: mdiAccountGroup
            },
            {
                id: 'marketplace-policy',
                title: 'Marketplace Policy',
                value: 220,
                link: {
                    personal: {
                        to: '/account/third-party-marketplace-policy',
                        permissions: false
                    },
                    entity: {
                        to: '/account/third-party-marketplace-policy',
                        permissions: {
                            page: 'marketplace_policy'
                        }
                    }
                },
                icon: mdiShieldHalfFull
            }
        ]
    },
    support: {
        title: 'Support',
        items: [
            {
                id: 'faq-main',
                title: 'Frequently Asked Questions',
                value: 230,
                link: {
                    personal: {
                        to: '/faq'
                    },
                    entity: {
                        to: '/faq'
                    }
                },
                icon: mdiFrequentlyAskedQuestions
            },
            {
                id: 'returns',
                title: 'Returns',
                value: 250,
                link: {
                    personal: {
                        to: '/account/returns'
                    },
                    entity: {
                        to: '/account/returns'
                    }
                },
                icon: mdiCashRefund
            },
            {
                id: 'contact',
                title: 'Contact Us',
                value: 260,
                link: {
                    personal: {
                        to: '/account/contact'
                    },
                    entity: {
                        to: '/account/contact'
                    }
                },
                icon: mdiPhoneMessage
            }
        ]
    }
};

export function useAccountNavData() {
    return {
        accountNavData: Object.assign({}, data)
    };
}
